import {
  globalDeleteService,
  globalGetService,
  globalPostService,
  globalPutService,
} from '@/utils/globalApiServices';
import { extractFilenameFromContentDisposition } from '@/utils/download';
import {
  Brand,
  BrandSuggestion,
  BrandSuggestionParams,
  Loa,
  Resend2faParams,
  VettingRecord,
  VettingRecordParams,
} from './types';
import { Pagination } from '@/utils/types';

export const getBrandSuggestions = async (
  params: BrandSuggestionParams
): Promise<BrandSuggestion[]> => {
  const response = await globalGetService('csp/brands/suggestions', params);
  return response?.ok ? (response.data as BrandSuggestion[]) : [];
};

export const getBrands = async (queries?: Record<string, any>) => {
  const response = await globalGetService('csp/brands', queries);
  return response?.ok ? (response.data as Pagination<Brand>) : undefined;
};

export const getBrand = async (brandUid: string) => {
  const response = await globalGetService(`csp/brands/${brandUid}`);
  return response?.ok ? (response.data as Brand) : undefined;
};

export const updateBrand = async (
  brandUid: string,
  data: Record<string, any>
) => {
  const response = await globalPutService(`csp/brands/${brandUid}`, data);
  return response?.ok ? (response.data as Brand) : undefined;
};

export const getVettingRecords = async (
  brandUid: string,
  params: VettingRecordParams = {}
): Promise<VettingRecord[]> => {
  const response = await globalGetService(
    `csp/brands/${brandUid}/vettings`,
    params
  );
  return (response?.ok ? response.data : []) as VettingRecord[];
};

export const applyVetting = async (
  brandUid: string,
  vettingClass: string,
  vettingProvider: string
) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/vettings/${vettingProvider}`,
    {
      vettingClass,
    }
  );
  return response?.ok ? (response.data as VettingRecord) : undefined;
};

export const createBrand = async (data: Record<string, unknown>) => {
  const response = await globalPostService('csp/brands', data);
  return response?.ok ? (response.data as Brand) : undefined;
};

export const createLoa = async (brandUid: string) => {
  const response = await globalPostService(`csp/brands/${brandUid}/LOA`);
  return response?.ok ? (response.data as Loa) : undefined;
};

export const getLoas = async (brandUid: string) => {
  const response = await globalGetService(`csp/brands/${brandUid}/LOA`);
  return (response?.ok ? response.data : []) as Loa[];
};

export const downloadLoa = async (brandUid: string, docUid: string) => {
  const response = await globalGetService(
    `csp/brands/${brandUid}/LOA/${docUid}`,
    { format: 'pdf' },
    { responseType: 'arraybuffer' }
  );
  if (response?.ok) {
    const filename = extractFilenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'LOA.pdf'
    );
    return {
      data: response.data as ArrayBuffer,
      filename,
    };
  }
  return undefined;
};

export const sendLoa = async (brandUid: string, docUid: string) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/LOA/${docUid}/send`
  );
  return response?.ok ? (response.data as Loa) : undefined;
};

export const cancelLoa = async (
  brandUid: string,
  docUid: string,
  configs = {}
) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/LOA/${docUid}/cancel`,
    undefined,
    configs
  );
  return !!response?.ok;
};

export const revet = async (brandUid: string) => {
  const response = await globalPutService(
    `csp/brands/${brandUid}/vet?nonBlocking=true`
  );
  return response?.ok ? (response.data as Brand) : undefined;
};

export const deleteBrand = async (brandUid: string) => {
  const response = await globalDeleteService(
    `/csp/brands/${brandUid}/softDelete`
  );
  return response?.ok ? (response.data as Brand) : undefined;
};

export const resend2FA = async (brandUid: string, params: Resend2faParams) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/2faEmail`,
    params
  );
  return !!response?.ok;
};
